/**
 * Two-line price breakdown shown under a price input.
 *
 * Line 1 — what the buyer is charged.
 * Line 2 — what the organizer actually receives.
 *
 * Splitting them is the whole point. "Absorb service fee" only changes WHICH side the
 * platform fee comes out of, and the single-sentence version left organizers guessing
 * whether the number they typed was what they'd be paid.
 *
 * Payment gateway (Stripe) fees are deducted on top of this and vary by payment method and
 * country, so they are named but deliberately never quantified — a number here would be a
 * guess, and organizers reconcile against Stripe, not against us.
 *
 * Shared by ticket pricing, paid add-ons and catalogue products so all three read the same.
 * Rendered inside TwInputGroup's <p>, so every element here must be inline (<span>), not a
 * <div> — a block element inside a <p> is auto-closed by the browser and breaks the layout.
 */
import React from "react";

/**
 * Platform service fee for an amount, from the organizer's bookzyPlatformFee config.
 * Same formula the backend uses in `calculateBookzyPlatformFee`.
 */
export const calculatePlatformServiceFee = (amount, bookzyPlatformFee) => {
  const value = parseFloat(amount);
  if (!(value > 0)) return 0;

  const flatFee = parseFloat(bookzyPlatformFee?.flatFee) > 0 ? parseFloat(bookzyPlatformFee.flatFee) : 0;
  const percentageFee =
    parseFloat(bookzyPlatformFee?.percentageFee) > 0 ? parseFloat(bookzyPlatformFee.percentageFee) : 0;

  return parseFloat((flatFee + value * (percentageFee / 100)).toFixed(2));
};

const money = (value) => parseFloat(parseFloat(value || 0).toFixed(2)).toFixed(2);

export default function PriceHelperText({
  t,
  amount,
  serviceFee = 0,
  /**
   * true  → organizer pays the service fee, buyer pays the listed price.
   * false → buyer pays the listed price plus the service fee.
   *
   * Products have no toggle: the organizer always absorbs, so they pass `true`.
   */
  absorbServiceFee = false,
  /** Extra sentence shown above the breakdown (e.g. the VAT note on products). */
  note,
}) {
  const price = parseFloat(amount) > 0 ? parseFloat(amount) : 0;
  const fee = parseFloat(serviceFee) > 0 ? parseFloat(serviceFee) : 0;

  const buyerPays = absorbServiceFee ? price : price + fee;
  // Never show a negative payout: the drawers already block a price below the fee, but a
  // half-typed value shouldn't flash "-€0.43" while the organizer is still typing.
  const payout = Math.max(absorbServiceFee ? price - fee : price, 0);

  return (
    <>
      {note ? <span className="block">{note}</span> : null}

      {/* No "including / excluding service fee" clause here. The buyer's price always has
          the service fee in it — absorbing only decides whether the listed price gets
          bumped up to cover it. Saying "excluding" was describing the organizer's side of
          the split on a line about the buyer. The fee is accounted for on the payout line. */}
      <span className="block">
        {t("myEvents.costToBuyer")} €{money(buyerPays)}
      </span>

      <span className="block">
        {t("myEvents.yourPayout", "Your payout:")} €{money(payout)}
        {fee > 0 && absorbServiceFee ? ` (€${money(price)} − €${money(fee)} ${t("myEvents.serviceFee")})` : ""}
        {t("myEvents.excludingGatewayFees", ", excluding payment gateway fees")}
      </span>
    </>
  );
}
